import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Eye, Edit2, CheckCircle2, Clock, AlertCircle, ChevronDown, Loader2,
} from "lucide-react";

export type ArticleStatus =
  | "draft" | "review" | "revision" | "approved" | "scheduled" | "published" | "archived";

export const STATUS_OPTIONS: {
  value: ArticleStatus; label: string; icon: React.ReactNode; classes: string;
}[] = [
  { value: "draft", label: "Draft", icon: <FileText className="h-3 w-3" />,
    classes: "bg-muted text-muted-foreground" },
  { value: "review", label: "In Review", icon: <Eye className="h-3 w-3" />,
    classes: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" },
  { value: "revision", label: "Revision", icon: <Edit2 className="h-3 w-3" />,
    classes: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
  { value: "approved", label: "Approved", icon: <CheckCircle2 className="h-3 w-3" />,
    classes: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" },
  { value: "scheduled", label: "Scheduled", icon: <Clock className="h-3 w-3" />,
    classes: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400" },
  { value: "published", label: "Published", icon: <CheckCircle2 className="h-3 w-3" />,
    classes: "bg-emerald-600 text-white" },
  { value: "archived", label: "Archived", icon: <AlertCircle className="h-3 w-3" />,
    classes: "bg-destructive/15 text-destructive" },
];

export const STATUS_MAP = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s])
) as Record<ArticleStatus, typeof STATUS_OPTIONS[number]>;

interface Props {
  value: ArticleStatus | string;
  onChange: (next: ArticleStatus) => void;
  disabled?: boolean;
  loading?: boolean;
}

export default function StatusPicker({ value, onChange, disabled, loading }: Props) {
  const current = STATUS_MAP[value as ArticleStatus] || STATUS_MAP.draft;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled || loading}>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full focus:outline-none focus:ring-2 focus:ring-ring/40 disabled:opacity-50"
          title="Click to change status"
        >
          <Badge className={`${current.classes} text-[11px] gap-1 font-medium border-transparent cursor-pointer hover:opacity-80 transition-opacity`}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : current.icon}
            {current.label}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        {STATUS_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onClick={() => opt.value !== value && onChange(opt.value)}
            className="text-xs gap-2 cursor-pointer"
          >
            <Badge className={`${opt.classes} text-[10px] gap-1 font-medium border-transparent`}>
              {opt.icon}
              {opt.label}
            </Badge>
            {opt.value === value && <CheckCircle2 className="h-3 w-3 ml-auto text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
